import { isEqual } from 'lodash';
import { styled } from '@mui/material';
import ResourcesNavigation from './ResourcesNavigation';
import AppliedFilters from './AppliedFilters';
import FiltersLoadError from './FiltersLoadError';
import Market from './Market';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  FilterSchema,
  getFilteredFiltersWithDetails,
  InitialFilterValues,
  ResourcesSidebarSections,
} from './ResourcesSidebarHelpers';
import Language from './Language';
import GameStatus from './GameStatus';
import Volatility from './Volatility';
import GameFeature from './GameFeature';
import GameProvider from './GameProvider';
import GameGroup from './GameGroup';
import FileFormatCompliance from './FileFormatCompliance';
import Rtp from './Rtp';
import FileTypeMarketing from './FileTypeMarketing';
import FileFormatMarketing from './FileFormatMarketing';
import FileTypeCompliance from './FileTypeCompliance';
import ReleaseDate from './ReleaseDate';
import GameFamily from './GameFamily';
import PartnerType from './PartnerType';
import Platform from './Platform';
import Operator from './Operator';
import Brand from './Brand';
import FilterListIcon from '@/assets/filter-list.svg?react';
import CancelIcon from '@/assets/cancel3.svg?react';
import { NavigationHeight } from '../Navigation/NavigationHelpers';
import { ComplianceFileTypeConfig } from '@/constants/FileTypesCompliance';
import { ga } from '@/lib/analytics';

export const RESOURCES_SIDEBAR_WIDTH = 380;

const ResourcesSidebar = ({
  visibleSections,
  onFiltersChange,
  initialValues,
  onOpenChange,
  forceOpenReleaseDate = false,
  fileTypeComplianceConfig,
}: {
  visibleSections: Partial<
    Record<ResourcesSidebarSections, boolean>
  > & {
    lessMarkets?: boolean;
  };
  onFiltersChange: (v: FilterSchema) => void;
  initialValues?: InitialFilterValues;
  onOpenChange: (isOpen: boolean) => void;
  forceOpenReleaseDate?: boolean;
  fileTypeComplianceConfig?: ComplianceFileTypeConfig;
}) => {
  const [selectedMarkets, setSelectedMarkets] = useState<
    number | null
  >(
    initialValues?.market && initialValues.market.length > 0
      ? initialValues.market[0]
      : null,
  );
  const [selectedGameStatus, setSelectedGameStatus] = useState<
    string[]
  >(initialValues?.gameStatus || []);

  const hasInitialFilters =
    !!initialValues &&
    Object.values(initialValues).some((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value),
    );

  const [isOpen, setIsOpen] = useState<boolean>(hasInitialFilters);

  /**
   * Wraps a filter setter so that picking a value in the sidebar reports a
   * `filter_apply` event. Clearing a filter reports nothing here — removing
   * filters goes through AppliedFilters, which has its own events.
   */
  const withFilterTracking = useCallback(
    <T,>(filterKey: string, setter: (value: T) => void) =>
      (value: T) => {
        setter(value);

        const filterType = ga.filterType(filterKey);
        const filterValue = ga.formatFilterValue(filterType, value);
        if (!filterValue) return;

        ga.trackFilterApply({
          filter_type: filterType,
          filter_value: filterValue,
        });
      },
    [],
  );

  useEffect(() => {
    onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    const fromUrl = initialValues?.gameStatus;
    if (
      fromUrl &&
      !isEqual(
        fromUrl.slice().sort(),
        selectedGameStatus.slice().sort(),
      )
    ) {
      setSelectedGameStatus(fromUrl);
    }
  }, [initialValues?.gameStatus]);

  const [selectedVolatility, setSelectedVolatility] = useState<
    [number, number][]
  >(
    initialValues?.volatility && initialValues.volatility.length === 2
      ? [
          [
            Number(initialValues.volatility[0]),
            Number(initialValues.volatility[1]),
          ],
        ]
      : [[1, 6]],
  );
  const [selectedLanguages, setSelectedLanguages] = useState<
    string[]
  >(initialValues?.language || []);
  const [selectedGameFeatures, setSelectedGameFeatures] = useState<
    string[]
  >(initialValues?.gameFeatures || []);
  const [selectedGameProviders, setSelectedGameProviders] = useState<
    string[]
  >(initialValues?.gameProviders || []);
  const [selectedGameGroups, setSelectedGameGroups] = useState<
    string[]
  >(initialValues?.gameGroups || []);
  const [selectedFileTypeCompliance, setSelectedFileTypeCompliance] =
    useState<string[]>(initialValues?.fileTypeCompliance || []);
  const [
    selectedFileFormatCompliance,
    setSelectedFileFormatCompliance,
  ] = useState<string[]>(initialValues?.fileFormatCompliance || []);
  const [selectedRtp, setSelectedRtp] = useState<string[][]>(
    initialValues?.rtp && initialValues.rtp.length > 0
      ? [initialValues.rtp[0]]
      : [['1', '100']],
  );
  const [selectedFileTypeMarketing, setSelectedFileTypeMarketing] =
    useState<string[]>(initialValues?.fileTypeMarketing || []);
  const [
    selectedFileFormatMarketing,
    setSelectedFileFormatMarketing,
  ] = useState<string[]>(initialValues?.fileFormatMarketing || []);
  const [selectedReleaseDate, setSelectedReleaseDate] = useState<
    string[]
  >(initialValues?.releaseDate || []);
  const [selectedGameFamilies, setSelectedGameFamilies] = useState<
    string[]
  >(initialValues?.gameFamily || []);
  const [selectedGames, setSelectedGames] = useState<
    (string | number)[]
  >([]);
  const [selectedPartnerTypes, setSelectedPartnerTypes] = useState<
    string[]
  >(initialValues?.partnerType || []);
  const [selectedPlatform, setSelectedPlatform] = useState<string[]>(
    initialValues?.platform || [],
  );
  const [selectedOperator, setSelectedOperator] = useState<string[]>(
    initialValues?.operator || [],
  );
  const [selectedBrandTypes, setSelectedBrandTypes] = useState<
    string[]
  >(initialValues?.brand || []);
  const detailedFilters = useMemo(
    () => ({
      market: {
        value: selectedMarkets !== null ? [selectedMarkets] : [],
        label: (
          <FormattedMessage id="market" defaultMessage="Market" />
        ),
        defaultValue: [],
        setter: (v: number[]) =>
          setSelectedMarkets(v.length > 0 ? v[0] : null),
      },
      language: {
        value: selectedLanguages,
        label: (
          <FormattedMessage id="language" defaultMessage="Language" />
        ),
        defaultValue: [],
        setter: setSelectedLanguages,
      },
      gameStatus: {
        value: selectedGameStatus,
        label: (
          <FormattedMessage
            id="gameStatus"
            defaultMessage="Game Status"
          />
        ),
        defaultValue: [],
        setter: setSelectedGameStatus,
      },
      volatility: {
        value: selectedVolatility,
        label: (
          <FormattedMessage
            id="volatility"
            defaultMessage="Volatility"
          />
        ),
        defaultValue: [[1, 6]],
        setter: setSelectedVolatility,
      },
      gameFeatures: {
        value: selectedGameFeatures,
        label: (
          <FormattedMessage
            id="gameFeatures"
            defaultMessage="Game Features"
          />
        ),
        defaultValue: [],
        setter: setSelectedGameFeatures,
      },
      gameProviders: {
        value: selectedGameProviders,
        label: (
          <FormattedMessage
            id="gameProvider"
            defaultMessage="Game Studio"
          />
        ),
        defaultValue: [],
        setter: setSelectedGameProviders,
      },
      gameGroups: {
        value: selectedGameGroups,
        label: (
          <FormattedMessage
            id="gameGroups"
            defaultMessage="Game Groups"
          />
        ),
        defaultValue: [],
        setter: setSelectedGameGroups,
      },
      fileTypeCompliance: {
        value: selectedFileTypeCompliance,
        label: (
          <FormattedMessage
            id="fileTypeCompliance"
            defaultMessage="File Type Compliance"
          />
        ),
        defaultValue: [],
        setter: setSelectedFileTypeCompliance,
      },
      fileFormatCompliance: {
        value: selectedFileFormatCompliance,
        label: (
          <FormattedMessage
            id="fileFormatCompliance"
            defaultMessage="File Format Compliance"
          />
        ),
        defaultValue: [],
        setter: setSelectedFileFormatCompliance,
      },
      rtp: {
        value: selectedRtp,
        label: <FormattedMessage id="rtp" defaultMessage="RTP" />,
        defaultValue: [['1', '100']],
        setter: setSelectedRtp,
      },
      fileTypeMarketing: {
        value: selectedFileTypeMarketing,
        label: (
          <FormattedMessage
            id="fileTypeMarketing"
            defaultMessage="File Type Marketing"
          />
        ),
        defaultValue: [],
        setter: setSelectedFileTypeMarketing,
      },
      fileFormatMarketing: {
        value: selectedFileFormatMarketing,
        label: (
          <FormattedMessage
            id="fileFormatMarketing"
            defaultMessage="File Format Marketing"
          />
        ),
        defaultValue: [],
        setter: setSelectedFileFormatMarketing,
      },
      releaseDate: {
        value: selectedReleaseDate,
        label: (
          <FormattedMessage
            id="releaseDate"
            defaultMessage="Release Date"
          />
        ),
        defaultValue: [],
        setter: setSelectedReleaseDate,
      },
      gameFamily: {
        value: selectedGameFamilies,
        label: (
          <FormattedMessage
            id="gameFamily"
            defaultMessage="Game Family"
          />
        ),
        defaultValue: [],
        setter: setSelectedGameFamilies,
      },
      game: {
        value: selectedGames,
        label: <FormattedMessage id="game" defaultMessage="Game" />,
        defaultValue: [],
        setter: setSelectedGames,
      },
      partnerType: {
        value: selectedPartnerTypes,
        label: (
          <FormattedMessage
            id="partnerType"
            defaultMessage="Partner Type"
          />
        ),
        defaultValue: [],
        setter: setSelectedPartnerTypes,
      },
      platform: {
        value: selectedPlatform,
        label: (
          <FormattedMessage
            id="platformType"
            defaultMessage="Platform Type"
          />
        ),
        defaultValue: [],
        setter: setSelectedPlatform,
      },
      operator: {
        value: selectedOperator,
        label: (
          <FormattedMessage
            id="operatorType"
            defaultMessage="Operator"
          />
        ),
        defaultValue: [],
        setter: setSelectedOperator,
      },
      brand: {
        value: selectedBrandTypes,
        label: (
          <FormattedMessage id="brandType" defaultMessage="Brand" />
        ),
        defaultValue: [],
        setter: setSelectedBrandTypes,
      },
    }),
    [
      selectedMarkets,
      selectedLanguages,
      selectedGameStatus,
      selectedVolatility,
      selectedGameFeatures,
      selectedGameProviders,
      selectedGameGroups,
      selectedFileTypeCompliance,
      selectedFileFormatCompliance,
      selectedRtp,
      selectedFileTypeMarketing,
      selectedFileFormatMarketing,
      selectedReleaseDate,
      selectedGameFamilies,
      selectedGames,
      selectedPartnerTypes,
      selectedPlatform,
      selectedOperator,
      selectedBrandTypes,
    ],
  );
  const filteredDetailedFilters = useMemo(
    () =>
      getFilteredFiltersWithDetails(detailedFilters, visibleSections),
    [detailedFilters, visibleSections],
  );
  const hasActiveFilters =
    Object.keys(filteredDetailedFilters).length > 0;
  const handleRemoveFilters = () => {
    Object.values(detailedFilters).forEach((filter) => {
      // @ts-expect-error - filter.setter is a function that takes a value of type T and returns void
      filter.setter(filter.defaultValue);
    });
  };

  const convertToFilterSchema = (
    filters: typeof filteredDetailedFilters,
  ): FilterSchema => {
    const result: Partial<FilterSchema> = {};

    Object.entries(filters).forEach(([section, filter]) => {
      const key = section as keyof FilterSchema;
      const value = filter.value;

      switch (key) {
        case 'navigation':
          if (typeof value === 'boolean') {
            result[key] = value;
          }
          break;
        case 'market':
          if (Array.isArray(value)) {
            result[key] = value
              .map((v) =>
                typeof v === 'string'
                  ? parseInt(v, 10)
                  : typeof v === 'number'
                    ? v
                    : NaN,
              )
              .filter((v): v is number => !isNaN(v));
          } else if (typeof value === 'number') {
            result[key] = [value];
          }
          break;
        case 'volatility': {
          const v = value?.[0] as [number, number];
          if (Array.isArray(value) && v?.length === 2) {
            result[key] = [String(v?.[0]), String(v?.[1])];
          }
          break;
        }
        case 'rtp':
          if (Array.isArray(value) && value.length === 1) {
            result[key] = value as string[][];
          }
          break;
        case 'releaseDate':
          if (Array.isArray(value)) {
            result[key] = value as string[];
          }
          break;
        default:
          if (Array.isArray(value)) {
            result[
              key as keyof Pick<
                FilterSchema,
                | 'fileTypeCompliance'
                | 'fileFormatCompliance'
                | 'fileTypeMarketing'
                | 'fileFormatMarketing'
                | 'partnerType'
                | 'platform'
                | 'operator'
                | 'brand'
                | 'language'
                | 'gameFamily'
                | 'gameStatus'
                | 'gameFeatures'
                | 'gameGroups'
                | 'gameProviders'
              >
            ] = value as string[];
          }
          break;
      }
    });

    return result as FilterSchema;
  };

  useEffect(() => {
    onFiltersChange(convertToFilterSchema(filteredDetailedFilters));
  }, [onFiltersChange, filteredDetailedFilters]);

  const filterSections: React.ReactNode[] = [];

  Object.entries(visibleSections).forEach(
    ([sectionName, isVisible]) => {
      if (!isVisible) {
        return;
      }

      if (
        sectionName === 'navigation' ||
        sectionName === 'lessMarkets'
      ) {
        return;
      }

      if (sectionName === 'releaseDate') {
        filterSections.push(
          <ReleaseDate
            key="releaseDate"
            value={selectedReleaseDate}
            onChange={withFilterTracking(
              'releaseDate',
              setSelectedReleaseDate,
            )}
            mode="dark"
            forceOpen={forceOpenReleaseDate}
          />,
        );
        return;
      }

      switch (sectionName as ResourcesSidebarSections) {
        case 'market':
          filterSections.push(
            <Market
              key="market"
              lessMarkets={visibleSections.lessMarkets ?? false}
              value={selectedMarkets}
              onChange={withFilterTracking(
                'market',
                setSelectedMarkets,
              )}
              mode="dark"
            />,
          );
          break;
        case 'language':
          filterSections.push(
            <Language
              key="language"
              value={selectedLanguages}
              onChange={withFilterTracking(
                'language',
                setSelectedLanguages,
              )}
              mode="dark"
            />,
          );
          break;
        case 'gameStatus':
          filterSections.push(
            <GameStatus
              key="gameStatus"
              value={selectedGameStatus}
              onChange={withFilterTracking(
                'gameStatus',
                setSelectedGameStatus,
              )}
              mode="dark"
            />,
          );
          break;
        case 'volatility':
          filterSections.push(
            <Volatility
              key="volatility"
              value={selectedVolatility}
              onChange={withFilterTracking(
                'volatility',
                setSelectedVolatility,
              )}
              mode="dark"
            />,
          );
          break;
        case 'gameFeatures':
          filterSections.push(
            <GameFeature
              key="gameFeatures"
              value={selectedGameFeatures}
              onChange={withFilterTracking(
                'gameFeatures',
                setSelectedGameFeatures,
              )}
              mode="dark"
            />,
          );
          break;
        case 'gameGroups':
          filterSections.push(
            <GameGroup
              key="gameGroups"
              value={selectedGameGroups}
              onChange={withFilterTracking(
                'gameGroups',
                setSelectedGameGroups,
              )}
              mode="dark"
            />,
          );
          break;
        case 'gameProviders':
          filterSections.push(
            <GameProvider
              key="gameProviders"
              value={selectedGameProviders}
              onChange={withFilterTracking(
                'gameProviders',
                setSelectedGameProviders,
              )}
              mode="dark"
            />,
          );
          break;
        case 'fileTypeCompliance':
          filterSections.push(
            <FileTypeCompliance
              key="fileTypeCompliance"
              fileTypeConfig={fileTypeComplianceConfig}
              value={selectedFileTypeCompliance}
              onChange={withFilterTracking(
                'fileTypeCompliance',
                setSelectedFileTypeCompliance,
              )}
              mode="dark"
            />,
          );
          break;
        case 'fileFormatCompliance':
          filterSections.push(
            <FileFormatCompliance
              key="fileFormatCompliance"
              value={selectedFileFormatCompliance}
              onChange={withFilterTracking(
                'fileFormatCompliance',
                setSelectedFileFormatCompliance,
              )}
              mode="dark"
            />,
          );
          break;
        case 'rtp':
          filterSections.push(
            <Rtp
              key="rtp"
              value={selectedRtp}
              onChange={withFilterTracking(
                'rtp',
                setSelectedRtp,
              )}
              mode="dark"
            />,
          );
          break;
        case 'fileTypeMarketing':
          filterSections.push(
            <FileTypeMarketing
              key="fileTypeMarketing"
              value={selectedFileTypeMarketing}
              onChange={withFilterTracking(
                'fileTypeMarketing',
                setSelectedFileTypeMarketing,
              )}
              mode="dark"
            />,
          );
          break;
        case 'fileFormatMarketing':
          filterSections.push(
            <FileFormatMarketing
              key="fileFormatMarketing"
              value={selectedFileFormatMarketing}
              onChange={withFilterTracking(
                'fileFormatMarketing',
                setSelectedFileFormatMarketing,
              )}
              mode="dark"
            />,
          );
          break;
        case 'gameFamily':
          filterSections.push(
            <GameFamily
              key="gameFamily"
              value={selectedGameFamilies}
              onChange={withFilterTracking(
                'gameFamily',
                setSelectedGameFamilies,
              )}
              mode="dark"
            />,
          );
          break;
        case 'partnerType':
          filterSections.push(
            <PartnerType
              key="partnerType"
              value={selectedPartnerTypes}
              onChange={withFilterTracking(
                'partnerType',
                setSelectedPartnerTypes,
              )}
              mode="dark"
            />,
          );
          break;
        case 'platform':
          filterSections.push(
            <Platform
              key="platform"
              value={selectedPlatform}
              onChange={withFilterTracking(
                'platform',
                setSelectedPlatform,
              )}
              mode="dark"
            />,
          );
          break;
        case 'operator':
          filterSections.push(
            <Operator
              key="operator"
              value={selectedOperator}
              onChange={withFilterTracking(
                'operator',
                setSelectedOperator,
              )}
              mode="dark"
            />,
          );
          break;
        case 'brand':
          filterSections.push(
            <Brand
              key="brand"
              value={selectedBrandTypes}
              onChange={withFilterTracking(
                'brand',
                setSelectedBrandTypes,
              )}
              mode="dark"
            />,
          );
          break;
        default:
          break;
      }
    },
  );

  return (
    <Root className="dark-scrollbar" aria-label="Resources sidebar">
      <SidebarContainer data-open={isOpen}>
        <Header>
          <HeaderLeft>
            <HeaderIconWrapper>
              <FilterListIcon />
            </HeaderIconWrapper>
            <HeaderTitle>
              <FormattedMessage
                id="filters"
                defaultMessage="Filter"
              />
            </HeaderTitle>
          </HeaderLeft>
          <CloseButton
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close filters"
          >
            <CloseIcon />
          </CloseButton>
        </Header>
        {visibleSections.navigation && <ResourcesNavigation />}
        <FiltersLoadError />
        <AppliedFilters
          filters={filteredDetailedFilters}
          onRemoveFilters={handleRemoveFilters}
        />
        {filterSections}
      </SidebarContainer>
      <ToggleButtonWrapper data-open={isOpen}>
        <ToggleButton
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Open filters"
          aria-expanded={isOpen}
        >
          <FilterListIcon />
          {hasActiveFilters && <ActiveDot />}
        </ToggleButton>
      </ToggleButtonWrapper>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  overflowY: 'auto',
  zIndex: theme.zIndexes.resourcesSidebar,
  pointerEvents: 'none',
  height: `calc(100vh - ${NavigationHeight}px)`,
}));

const SidebarContainer = styled('div')(({ theme }) => ({
  width: RESOURCES_SIDEBAR_WIDTH,
  display: 'flex',
  height: '100%',
  pointerEvents: 'all',
  color: theme.palette.white,
  padding: '0 20px 0 40px',
  overflowY: 'auto',
  flexDirection: 'column',
  transition: 'transform 0.25s, opacity 0.25s',
  transitionDelay: '0.1s, 0.1s',
  transform: 'translateX(0)',
  background: theme.palette.black80o,
  opacity: 1,
  '&[data-open="false"]': {
    transform: 'translateX(-100%)',
    opacity: 0,
    pointerEvents: 'none',
  },
}));

const Header = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 0 0 0',
}));

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const HeaderIconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeaderTitle = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
  color: theme.palette.white,
}));

const CloseButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 16,
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  color: theme.palette.white,
}));

const CloseIcon = styled(CancelIcon)(({ theme }) => ({
  '& path': {
    stroke: theme.palette.white,
  },
}));

const ToggleButtonWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  zIndex: theme.zIndexes.resourcesSidebar,
  top: 24,
  left: 0,
  height: 72,
  width: 72,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  transition: 'transform 0.25s, opacity 0.25s',
  transform: 'translateX(-100%)',
  opacity: 0,
  pointerEvents: 'none',
  '&[data-open="false"]': {
    transform: 'translateX(0)',
    opacity: 1,
    pointerEvents: 'auto',
  },
}));

const ToggleButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: 72,
  height: 72,
  border: 'none',
  borderRadius: '0 36px 36px 0',
  backgroundColor: theme.palette.black80o,
  cursor: 'pointer',
  padding: 0,
  color: theme.palette.white,
}));

const ActiveDot = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 24,
  right: 20,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.red300,
}));

export default ResourcesSidebar;
